import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

// --- Keyframes Definition ---
const icyTrailAnimation = keyframes`
  0%, 100% {
    stroke: #b3f0ff;
    transform: scaleY(1) scaleX(1);
    filter: drop-shadow(0 0 2px #b3f0ff);
  }
  50% {
    stroke: #e6faff;
    transform: scaleY(1.25) scaleX(1.15);
    filter: drop-shadow(0 0 6px #e6faff) drop-shadow(0 0 10px #b3f0ff);
  }
`;

const trailIgniteAnimation = keyframes`
  0% {
    transform: scaleY(0) scaleX(0.3);
    opacity: 0;
  }
  30% {
    transform: scaleY(0.7) scaleX(0.8);
    opacity: 0.7;
  }
  100% {
    transform: scaleY(1) scaleX(1);
    opacity: 1;
  }
`;

const rocketLiftoffAnimation = keyframes`
  0% { transform: translateY(0) rotate(0); opacity: 1; }
  15% { transform: translateY(-10px); opacity: 1; }
  85% { transform: translateY(-350px); opacity: 1; }
  100% { transform: translateY(-450px); opacity: 0; }
`;

// --- Styled Components ---
const StyledLoader = styled.div`
  ${({ theme }) => theme.mixins?.flexCenter || `
    display: flex;
    justify-content: center;
    align-items: center;
  `};
  position: fixed;
  inset: 0;
  background-color: #0d1117;
  z-index: 99;
  opacity: ${props => (props.isMounted && !props.isExiting ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;

  .logo-wrapper {
    width: 120px;
    height: 300px;
    opacity: ${props => (props.isMounted ? 1 : 0)};
    transition: opacity 0.5s 0.2s ease-in;
    overflow: visible;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  .a-launch-logo-container {
    width: 100%;
    height: auto;
    overflow: visible;

    .rocket-component {
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
      transition: filter 0.4s ease-out, opacity 0.4s ease-out;
    }

    .rocket-body-main,
    .rocket-fin,
    .rocket-body-band,
    .rocket-engine-nozzle,
    .rocket-window-frame {
      stroke: #b3f0ff;
      filter: drop-shadow(0 0 2px #b3f0ff);
      opacity: 0;
    }

    .rocket-window-glass {
      // fill: #e6f1ff;
      // filter: drop-shadow(0 0 1.5px #e6f1ff);
      opacity: 0;
      transition: filter 0.4s ease-out, opacity 0.4s ease-out, fill 0.4s ease-out;
    }

    &.launch-active .rocket-body-main,
    &.launch-active .rocket-fin,
    &.launch-active .rocket-body-band,
    &.launch-active .rocket-engine-nozzle,
    &.launch-active .rocket-window-frame {
      opacity: 1;
      filter: drop-shadow(0 0 4px #b3f0ff) drop-shadow(0 0 10px #e6faff);
    }

    &.launch-active .rocket-window-glass {
      opacity: 0.85;
      fill: #0d1117;
      filter: drop-shadow(0 0 6px #b3f0ff);
    }

    .rocket-trail {
      stroke: #b3f0ff;
      stroke-width: 10;
      opacity: 0;
      transform-origin: 50px 118px;
    }

    &.launch-active .rocket-trail {
      animation:
        ${trailIgniteAnimation} 0.6s forwards ease-out,
        ${icyTrailAnimation} 1.4s 0.5s ease-in-out infinite;
    }

    &.launch-active #rocket-assembly {
      animation: ${rocketLiftoffAnimation} 1s cubic-bezier(0.6, 0.04, 0.98, 0.335) 0.8s forwards;
    }
  }
`;

// --- React Components ---
const LoaderRocket = React.memo(({ isLaunching }) => {
  return (
    <div className={`a-launch-logo-container ${isLaunching ? 'launch-active' : ''}`}>
      <svg
        className="a-launch-logo-svg"
        viewBox="0 0 100 200"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMin meet"
        style={{ display: 'block', overflow: 'visible' }}
      >
        <g id="rocket-assembly" transform="translate(0 60)">
          <path className="rocket-component rocket-trail" d="M 42 118 Q 50 135 58 118 Z" />
          <g id="rocket-main-structure">
            <path className="rocket-component rocket-engine-nozzle" d="M 40 110 L 42 118 L 58 118 L 60 110 Z" />
            <path className="rocket-component rocket-body-main" d="M 50 20 L 30 100 L 40 110 H 60 L 70 100 L 50 20 Z" />
            <path className="rocket-component rocket-body-band" d="M 38 70 H 62" />
            <circle className="rocket-component rocket-window-glass" cx="50" cy="55" r="6" />
            <circle className="rocket-component rocket-window-frame" cx="50" cy="55" r="7" />
            <path className="rocket-component rocket-fin" d="M 30 85 L 15 115 L 35 105 Z" />
            <path className="rocket-component rocket-fin" d="M 70 85 L 85 115 L 65 105 Z" />
          </g>
        </g>
      </svg>
    </div>
  );
});

LoaderRocket.propTypes = {
  isLaunching: PropTypes.bool.isRequired,
};
LoaderRocket.displayName = 'LoaderRocket';

const Loader = ({ finishLoading }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [startLaunchSequence, setStartLaunchSequence] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const mountTimer = setTimeout(() => setIsMounted(true), 10);
    const launchTimer = setTimeout(() => setStartLaunchSequence(true), 500); 

    const totalAnimationTime = 2000; 

    let finishSequenceTimer = null;
    let unmountTimer = null;

    finishSequenceTimer = setTimeout(() => {
      setIsExiting(true);
      unmountTimer = setTimeout(() => finishLoading(), 500); 
    }, totalAnimationTime);

    return () => {
      clearTimeout(mountTimer);
      clearTimeout(launchTimer);
      clearTimeout(finishSequenceTimer);
      clearTimeout(unmountTimer);
    };
  }, [finishLoading]);

  return (
    <StyledLoader className="loader" isMounted={isMounted} isExiting={isExiting}>
      <Helmet bodyAttributes={{ class: `hidden` }} />
      <div className="logo-wrapper">
        <LoaderRocket isLaunching={startLaunchSequence} />
      </div>
    </StyledLoader>
  );
};

Loader.propTypes = {
  finishLoading: PropTypes.func.isRequired,
};

export default Loader;
